import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, afterNextRender, computed, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

type Status = 'Active' | 'Inactive';
type Option = { code: string; name: string };
type Link = Option & { pssCode: string };
type Contract = { id: number; buyerName: string; contractType: string; contractCapacityMw: number; pssCode: string; pssName: string };
type PathRow = { id: string; name: string; code: string; description: string; pssCode: string; owner: string; districts: string[]; linkCodes: string[]; contractIds: number[]; purpose: string; usedForDataSharing: boolean; effectiveFrom: string; effectiveTo: string; remarks: string; status: Status };
type PathMock = { header: { title: string; description: string }; owners: string[]; districts: string[]; links: Link[]; paths: PathRow[] };
type ContractMock = { pssOptions: Option[]; contractMappings: Contract[] };
type NetworkEdge = { source: string; target: string };
type NetworkLine = NetworkEdge & { x1: number; y1: number; x2: number; y2: number };
type NetworkHighlight = { nodes: Set<string>; edges: Set<string> };
type MultiSelectName = 'districts' | 'links' | 'contracts';

const EMPTY_PATH_DATA: PathMock = { header: { title: 'Path Master', description: 'Manage evacuation network Path details for PSS.' }, owners: [], districts: [], links: [], paths: [] };
const EMPTY_CONTRACT_DATA: ContractMock = { pssOptions: [], contractMappings: [] };

@Component({
  selector: 'app-path-master',
  imports: [ReactiveFormsModule],
  templateUrl: './path-master.component.html',
  styleUrls: ['./path-master.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PathMasterComponent implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);

  readonly pathData = signal<PathMock>(EMPTY_PATH_DATA);
  readonly contractData = signal<ContractMock>(EMPTY_CONTRACT_DATA);
  readonly rows = signal<PathRow[]>([]);
  readonly activeTab = signal<'path' | 'link' | 'district' | 'flow'>('path');
  readonly searchTerm = signal('');
  readonly selectedPssCode = signal('');
  readonly isFormOpen = signal(false);
  readonly formError = signal<string | null>(null);
  readonly toast = signal<string | null>(null);
  readonly hoveredNetworkNode = signal<string | null>(null);
  readonly selectedNetworkNode = signal<string | null>(null);
  readonly networkLines = signal<NetworkLine[]>([]);
  readonly multiSelectOpen = signal<Record<MultiSelectName, boolean>>({ districts: false, links: false, contracts: false });
  private readonly networkCanvas = viewChild<ElementRef<HTMLElement>>('networkCanvas');
  private resizeObserver?: ResizeObserver;
  private renderFrameId: number | null = null;
  private settleTimerId: number | null = null;

  readonly title = computed(() => this.route.snapshot.data['title'] as string || this.pathData().header.title);
  readonly description = computed(() => this.route.snapshot.data['description'] as string || this.pathData().header.description);
  readonly pssOptions = computed(() => this.contractData().pssOptions);
  readonly availablePssOptions = computed(() => this.pssOptions().filter((pss) => this.pathData().links.some((link) => link.pssCode === pss.code)));
  readonly linksForPss = computed(() => this.pathData().links.filter((link) => link.pssCode === this.selectedPssCode()));
  readonly contractsForPss = computed(() => this.contractData().contractMappings.filter((contract) => contract.pssCode === this.selectedPssCode()));
  readonly filteredRows = computed(() => {
    const search = this.searchTerm().trim().toLowerCase();
    return this.rows().filter((row) => !search || [row.id, row.name, row.code, this.pssName(row.pssCode), row.purpose].some((value) => value.toLowerCase().includes(search)));
  });
  readonly hierarchyPss = computed(() => this.pssOptions().filter((pss) => this.pathData().links.some((link) => link.pssCode === pss.code)));
  readonly hierarchyDistricts = computed(() => [...new Set(this.rows().flatMap((row) => row.districts))]);
  readonly hierarchyFlowAreas = computed(() => [...new Set(this.rows().flatMap((row) => this.flowAreasFor(row)))]);
  readonly networkEdges = computed<NetworkEdge[]>(() => {
    const edges: NetworkEdge[] = [];
    const add = (source: string, target: string) => edges.push({ source, target });
    this.pathData().links.forEach((link) => add(this.networkNodeId('pss', link.pssCode), this.networkNodeId('link', link.code)));
    this.rows().forEach((path) => {
      path.linkCodes.forEach((linkCode) => add(this.networkNodeId('link', linkCode), this.networkNodeId('path', path.id)));
      path.districts.forEach((district) => {
        add(this.networkNodeId('path', path.id), this.networkNodeId('district', district));
        this.flowAreasFor(path).forEach((flow) => add(this.networkNodeId('district', district), this.networkNodeId('flow', flow)));
      });
    });
    return edges;
  });
  readonly activeNetworkNode = computed(() => this.hoveredNetworkNode() || this.selectedNetworkNode());
  readonly networkHighlight = computed<NetworkHighlight>(() => {
    const active = this.activeNetworkNode();
    if (!active) return { nodes: new Set<string>(), edges: new Set<string>() };
    const nodes = new Set<string>([active]);
    const edges = new Set<string>();
    const separator = active.indexOf(':');
    const type = active.slice(0, separator);
    const value = active.slice(separator + 1);
    const addEdge = (source: string, target: string): void => {
      nodes.add(source);
      nodes.add(target);
      edges.add(this.networkEdgeKey({ source, target }));
    };
    const addUpstream = (path: PathRow, linkCodes = path.linkCodes): void => {
      linkCodes.forEach((linkCode) => {
        const link = this.pathData().links.find((item) => item.code === linkCode);
        if (!link) return;
        addEdge(this.networkNodeId('pss', link.pssCode), this.networkNodeId('link', link.code));
        addEdge(this.networkNodeId('link', link.code), this.networkNodeId('path', path.id));
      });
    };
    const addDownstream = (path: PathRow, districts = path.districts, flows = this.flowAreasFor(path)): void => {
      districts.forEach((district) => {
        addEdge(this.networkNodeId('path', path.id), this.networkNodeId('district', district));
        flows.forEach((flow) => addEdge(this.networkNodeId('district', district), this.networkNodeId('flow', flow)));
      });
    };

    if (type === 'pss') {
      const linkCodes = this.pathData().links.filter((link) => link.pssCode === value).map((link) => link.code);
      linkCodes.forEach((linkCode) => addEdge(active, this.networkNodeId('link', linkCode)));
      this.rows().filter((path) => path.linkCodes.some((linkCode) => linkCodes.includes(linkCode))).forEach((path) => {
        path.linkCodes.filter((linkCode) => linkCodes.includes(linkCode)).forEach((linkCode) => addEdge(this.networkNodeId('link', linkCode), this.networkNodeId('path', path.id)));
        addDownstream(path);
      });
    } else if (type === 'link') {
      const link = this.pathData().links.find((item) => item.code === value);
      if (link) addEdge(this.networkNodeId('pss', link.pssCode), active);
      this.rows().filter((path) => path.linkCodes.includes(value)).forEach((path) => {
        addEdge(active, this.networkNodeId('path', path.id));
        addDownstream(path);
      });
    } else if (type === 'path') {
      const path = this.rows().find((item) => item.id === value);
      if (path) {
        addUpstream(path);
        addDownstream(path);
      }
    } else if (type === 'district') {
      this.rows().filter((path) => path.districts.includes(value)).forEach((path) => {
        addUpstream(path);
        addDownstream(path, [value]);
      });
    } else if (type === 'flow') {
      this.rows().filter((path) => this.flowAreasFor(path).includes(value)).forEach((path) => {
        addUpstream(path);
        addDownstream(path, path.districts, [value]);
      });
    }
    return { nodes, edges };
  });

  readonly form = this.fb.nonNullable.group({
    pssCode: ['', Validators.required],
    name: ['', Validators.required],
    code: ['', Validators.required],
    description: ['', [Validators.required, Validators.maxLength(250)]],
    owner: ['', Validators.required],
    districts: [[] as string[], Validators.required],
    linkCodes: [[] as string[], Validators.required],
    contractIds: [[] as number[]],
    purpose: ['', Validators.required],
    usedForDataSharing: [true],
    effectiveFrom: ['', Validators.required],
    effectiveTo: [''],
    remarks: ['', Validators.maxLength(250)],
    status: ['Active' as Status, Validators.required]
  });

  constructor() {
    afterNextRender(() => {
      this.observeNetworkCanvas();
      this.scheduleNetworkLines({ settle: true });
    });

    this.http.get<PathMock>('json/path-master.mock.json').subscribe({ next: (data) => { this.pathData.set(data); this.rows.set(data.paths); this.scheduleNetworkLines({ settle: true }); }, error: () => this.pathData.set(EMPTY_PATH_DATA) });
    this.http.get<ContractMock>('json/contract-mapping.mock.json').subscribe({ next: (data) => { this.contractData.set(data); this.scheduleNetworkLines({ settle: true }); }, error: () => this.contractData.set(EMPTY_CONTRACT_DATA) });
    this.form.controls.pssCode.valueChanges.subscribe((pssCode) => {
      this.selectedPssCode.set(pssCode);
      this.form.patchValue({ linkCodes: [], contractIds: [] }, { emitEvent: false });
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.renderFrameId !== null) {
      cancelAnimationFrame(this.renderFrameId);
    }
    if (this.settleTimerId !== null) {
      window.clearTimeout(this.settleTimerId);
    }
  }

  openCreateForm(): void { this.form.reset({ pssCode: '', name: '', code: '', description: '', owner: '', districts: [], linkCodes: [], contractIds: [], purpose: '', usedForDataSharing: true, effectiveFrom: '', effectiveTo: '', remarks: '', status: 'Active' }); this.selectedPssCode.set(''); this.multiSelectOpen.set({ districts: false, links: false, contracts: false }); this.formError.set(null); this.isFormOpen.set(true); }
  closeForm(): void { this.isFormOpen.set(false); this.formError.set(null); }
  selectTab(tab: 'path' | 'link' | 'district' | 'flow'): void { this.activeTab.set(tab); }
  updateSearch(value: string): void { this.searchTerm.set(value); }
  pssName(code: string): string { return this.pssOptions().find((pss) => pss.code === code)?.name || code; }
  linkName(code: string): string { return this.pathData().links.find((link) => link.code === code)?.name || code; }
  linkNames(codes: string[]): string { return codes.map((code) => this.linkName(code)).join(', '); }
  districtSelectionLabel(): string { return this.form.controls.districts.value.length ? `${this.form.controls.districts.value.length} district${this.form.controls.districts.value.length === 1 ? '' : 's'} selected` : 'Select district(s)'; }
  linkSelectionLabel(): string { return this.form.controls.linkCodes.value.length ? `${this.form.controls.linkCodes.value.length} link${this.form.controls.linkCodes.value.length === 1 ? '' : 's'} selected` : 'Select link(s)'; }
  contractSelectionLabel(): string { return this.form.controls.contractIds.value.length ? `${this.form.controls.contractIds.value.length} contract${this.form.controls.contractIds.value.length === 1 ? '' : 's'} selected` : 'Select contract(s)'; }
  toggleDistrict(district: string, checked: boolean): void { this.form.controls.districts.setValue(this.updateSelection(this.form.controls.districts.value, district, checked)); }
  toggleLink(code: string, checked: boolean): void { this.form.controls.linkCodes.setValue(this.updateSelection(this.form.controls.linkCodes.value, code, checked)); }
  toggleContract(id: number, checked: boolean): void { this.form.controls.contractIds.setValue(this.updateSelection(this.form.controls.contractIds.value, id, checked)); }
  isMultiSelectOpen(name: MultiSelectName): boolean { return this.multiSelectOpen()[name]; }
  setMultiSelectOpen(name: MultiSelectName, open: boolean): void { this.multiSelectOpen.update((state) => ({ ...state, [name]: open })); }
  closeMultiSelectOnFocusOut(name: MultiSelectName, event: FocusEvent): void {
    const details = event.currentTarget as HTMLDetailsElement;
    queueMicrotask(() => {
      if (!details.contains(document.activeElement)) this.setMultiSelectOpen(name, false);
    });
  }
  pathCountForPss(pssCode: string): number { return this.rows().filter((row) => row.pssCode === pssCode).length; }
  pathNameForLink(linkCode: string): string { return this.rows().find((row) => row.linkCodes.includes(linkCode))?.name || 'Not mapped'; }
  networkNodeId(type: 'pss' | 'link' | 'path' | 'district' | 'flow', value: string): string { return `${type}:${value}`; }
  setHoveredNetworkNode(nodeId: string | null): void { this.hoveredNetworkNode.set(nodeId); }
  selectNetworkNode(nodeId: string): void { this.selectedNetworkNode.update((selected) => selected === nodeId ? null : nodeId); }
  clearNetworkSelection(): void { this.selectedNetworkNode.set(null); }
  isNetworkNodeHighlighted(nodeId: string): boolean { return !this.activeNetworkNode() || this.networkHighlight().nodes.has(nodeId); }
  isNetworkEdgeHighlighted(edge: NetworkEdge): boolean { return !!this.activeNetworkNode() && this.networkHighlight().edges.has(this.networkEdgeKey(edge)); }
  isNetworkEdgeDimmed(edge: NetworkEdge): boolean { return !!this.hoveredNetworkNode() && !this.isNetworkEdgeHighlighted(edge); }
  networkMarker(edge: NetworkEdge): string { return this.isNetworkEdgeHighlighted(edge) ? 'url(#network-line-arrow-highlighted)' : 'url(#network-line-arrow)'; }
  networkPath(edge: NetworkLine): string {
    return `M ${edge.x1} ${edge.y1} C ${edge.x1 + 42} ${edge.y1}, ${edge.x2 - 42} ${edge.y2}, ${edge.x2} ${edge.y2}`;
  }
  networkFlowArrowTransform(edge: NetworkLine): string {
    const p0 = { x: edge.x1, y: edge.y1 };
    const p1 = { x: edge.x1 + 42, y: edge.y1 };
    const p2 = { x: edge.x2 - 42, y: edge.y2 };
    const p3 = { x: edge.x2, y: edge.y2 };
    const t = 0.5;
    const point = this.cubicBezierPoint(p0, p1, p2, p3, t);
    const tangent = this.cubicBezierTangent(p0, p1, p2, p3, t);
    const angle = Math.atan2(tangent.y, tangent.x) * (180 / Math.PI);
    return `translate(${point.x} ${point.y}) rotate(${angle})`;
  }
  contractsFor(row: PathRow): Contract[] { return this.contractData().contractMappings.filter((contract) => row.contractIds.includes(contract.id)); }
  flowAreasFor(row: PathRow): string[] { return [...new Set(this.contractsFor(row).map((contract) => `${contract.buyerName} Flow Area`))]; }

  private updateSelection<T>(values: T[], value: T, checked: boolean): T[] {
    return checked ? [...new Set([...values, value])] : values.filter((item) => item !== value);
  }

  private networkEdgeKey(edge: NetworkEdge): string {
    return `${edge.source}->${edge.target}`;
  }

  save(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); this.formError.set('Please fill all mandatory Path and mapping details.'); return; }
    const value = this.form.getRawValue();
    if (value.effectiveTo && value.effectiveFrom > value.effectiveTo) { this.formError.set('Effective To Date must be after Effective From Date.'); return; }
    const validLinks = this.linksForPss().map((link) => link.code);
    const validContracts = this.contractsForPss().map((contract) => contract.id);
    if (value.linkCodes.some((code) => !validLinks.includes(code)) || value.contractIds.some((id) => !validContracts.includes(id))) { this.formError.set('Links and contracts must belong to the selected PSS.'); return; }
    const row: PathRow = { ...value, id: `PT${String(this.rows().length + 1).padStart(3, '0')}` };
    this.rows.update((rows) => [row, ...rows]);
    this.scheduleNetworkLines({ settle: true });
    this.closeForm(); this.toast.set(`${row.name} was added successfully.`); window.setTimeout(() => this.toast.set(null), 3500);
  }

  private observeNetworkCanvas(): void {
    const canvas = this.networkCanvas()?.nativeElement;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.scheduleNetworkLines());
    this.resizeObserver.observe(canvas);
  }

  private scheduleNetworkLines(options: { settle?: boolean } = {}): void {
    if (this.renderFrameId !== null) {
      cancelAnimationFrame(this.renderFrameId);
    }
    this.renderFrameId = requestAnimationFrame(() => {
      this.renderFrameId = requestAnimationFrame(() => {
        this.renderFrameId = null;
        this.drawNetworkLines();
      });
    });

    if (options.settle) {
      if (this.settleTimerId !== null) {
        window.clearTimeout(this.settleTimerId);
      }
      this.settleTimerId = window.setTimeout(() => {
        this.settleTimerId = null;
        this.drawNetworkLines();
      }, 160);
    }
  }

  private drawNetworkLines(): void {
    const canvas = this.networkCanvas()?.nativeElement;
    if (!canvas) return;
    const bounds = canvas.getBoundingClientRect();
    const nodes = new Map(Array.from(canvas.querySelectorAll<HTMLElement>('[data-network-node]')).map((element) => [element.dataset['networkNode']!, element]));
    this.networkLines.set(this.networkEdges().flatMap((edge) => {
      const source = nodes.get(edge.source)?.getBoundingClientRect();
      const target = nodes.get(edge.target)?.getBoundingClientRect();
      if (!source || !target) return [];
      return [{ ...edge, x1: source.right - bounds.left, y1: source.top + source.height / 2 - bounds.top, x2: target.left - bounds.left, y2: target.top + target.height / 2 - bounds.top }];
    }));
  }

  private cubicBezierPoint(p0: { x: number; y: number }, p1: { x: number; y: number }, p2: { x: number; y: number }, p3: { x: number; y: number }, t: number): { x: number; y: number } {
    const mt = 1 - t;
    return {
      x: (mt ** 3 * p0.x) + (3 * mt ** 2 * t * p1.x) + (3 * mt * t ** 2 * p2.x) + (t ** 3 * p3.x),
      y: (mt ** 3 * p0.y) + (3 * mt ** 2 * t * p1.y) + (3 * mt * t ** 2 * p2.y) + (t ** 3 * p3.y)
    };
  }

  private cubicBezierTangent(p0: { x: number; y: number }, p1: { x: number; y: number }, p2: { x: number; y: number }, p3: { x: number; y: number }, t: number): { x: number; y: number } {
    const mt = 1 - t;
    return {
      x: (3 * mt ** 2 * (p1.x - p0.x)) + (6 * mt * t * (p2.x - p1.x)) + (3 * t ** 2 * (p3.x - p2.x)),
      y: (3 * mt ** 2 * (p1.y - p0.y)) + (6 * mt * t * (p2.y - p1.y)) + (3 * t ** 2 * (p3.y - p2.y))
    };
  }
}
